import {
  OrderWorkflowNodeConditionCheckType as CheckType,
  OrderWorkflowNodeConditionComparisonType as Comparison
} from "lib/types/generated/graphql"
import {
  BILLING_ADDRESS_EMAIL,
  ORDER_LINE_PRODUCT_PARENT_ID,
  ORDER_LINE_PRODUCT_VARIANT_ID,
  PAYMENT_PROVIDER_NAME
} from "helpers/workflowConverter"
import { CHECK_KIND_LEAF, CheckLeaf } from "lib/types/workflow"

// Re-exported for convenience so callers can import a single set of UI-side
// check-type identifiers from one place.
export {
  PAYMENT_PROVIDER_NAME,
  ORDER_LINE_PRODUCT_PARENT_ID,
  ORDER_LINE_PRODUCT_VARIANT_ID
}

export const CUSTOM_HTTP = CheckType.CustomHttp

export const CHECK_TYPE_GROUPS: {
  label: string
  options: { value: string; label: string }[]
}[] = [
  {
    label: "Provider checks",
    options: [{ value: PAYMENT_PROVIDER_NAME, label: "Payment Provider" }]
  },
  {
    label: "Order line checks",
    options: [
      { value: ORDER_LINE_PRODUCT_PARENT_ID, label: "Product Parent ID" },
      { value: ORDER_LINE_PRODUCT_VARIANT_ID, label: "Product Variant ID" }
    ]
  },
  {
    label: "Order-level checks",
    options: [{ value: BILLING_ADDRESS_EMAIL, label: "Billing Address — Email list" }]
  },
  {
    label: "Custom",
    options: [{ value: CUSTOM_HTTP, label: "Custom HTTP Request" }]
  }
]

export const COMPARISONS = [
  { value: Comparison.Equals, label: "Equals" },
  { value: Comparison.NotEquals, label: "Not Equals" },
  { value: Comparison.Contains, label: "Contains" },
  { value: Comparison.NotContains, label: "Not Contains" },
  { value: Comparison.BeginsWith, label: "Begins With" },
  { value: Comparison.EndsWith, label: "Ends With" }
]

// Only PAYMENT_PROVIDER_NAME is restricted to equality comparisons; product
// parent/variant ID checks accept the full set so users can match on prefixes
// or substrings (matches master's narrowed restriction from PR #752).
export const EQUALITY_ONLY_CHECK_TYPES = new Set<string>([PAYMENT_PROVIDER_NAME])

export const getComparisonsForCheckType = (checkType: string) =>
  EQUALITY_ONLY_CHECK_TYPES.has(checkType)
    ? COMPARISONS.filter(
        (c) => c.value === Comparison.Equals || c.value === Comparison.NotEquals
      )
    : COMPARISONS

export const parseEmailList = (raw: string): string[] =>
  Array.from(
    new Set(
      raw
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    )
  )

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const findInvalidEmails = (emails: string[]): string[] =>
  emails.filter((e) => !EMAIL_RE.test(e))

export const MAX_EMAILS = 100

export const isValidJson = (value: string): boolean => {
  if (!value.trim()) return true
  try {
    JSON.parse(value)
    return true
  } catch {
    return false
  }
}

const HTTPS_RE = /^https:\/\/.+/

export const isLeafValid = (leaf: CheckLeaf): boolean => {
  if (leaf.checkType === CUSTOM_HTTP) {
    if (!leaf.endpoint || !HTTPS_RE.test(leaf.endpoint)) return false
    if (!isValidJson(leaf.customData ?? "")) return false
    return true
  }
  if (leaf.checkType === BILLING_ADDRESS_EMAIL) {
    const emails = leaf.values ?? []
    if (emails.length === 0 || emails.length > MAX_EMAILS) return false
    if (findInvalidEmails(emails).length > 0) return false
    return true
  }
  if (leaf.checkType === PAYMENT_PROVIDER_NAME) {
    return (leaf.values ?? []).length > 0
  }
  // Generic single/multi-value — at least one non-empty value.
  return (leaf.values ?? []).some((v) => v.trim().length > 0)
}

export const makeDefaultLeaf = (): CheckLeaf => ({
  kind: CHECK_KIND_LEAF,
  checkType: PAYMENT_PROVIDER_NAME,
  comparison: Comparison.Equals,
  values: []
})
